// src/components/activity/UserActivityDashboard.jsx

import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, where, limit, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { GLOBAL_ACTIVITY_MESSAGES } from '../../utils/globalActivityLogger';
import * as XLSX from 'xlsx';

const styles = {
  container: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    background: '#f8fafc',
    minHeight: '100vh',
    padding: '24px'
  },
  header: {
    background: 'white',
    borderRadius: '16px',
    padding: '32px',
    marginBottom: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    border: '1px solid #e2e8f0'
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#0f172a',
    margin: '0 0 8px 0'
  },
  subtitle: {
    fontSize: '16px',
    color: '#64748b',
    margin: 0
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginBottom: '32px'
  },
  statCard: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    border: '1px solid #e2e8f0',
    transition: 'transform 0.2s ease'
  },
  statNumber: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#0f172a',
    margin: '0 0 8px 0'
  },
  statLabel: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0
  },
  filterSection: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    border: '1px solid #e2e8f0'
  },
  filterGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    alignItems: 'end'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column'
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '8px'
  },
  select: {
    padding: '12px 16px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    background: 'white',
    outline: 'none',
    transition: 'border-color 0.2s'
  },
  input: {
    padding: '12px 16px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    background: 'white',
    outline: 'none',
    transition: 'border-color 0.2s'
  },
  button: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #06C167, #8b5cf6)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  exportButton: {
    padding: '12px 24px',
    background: 'white',
    color: '#06C167',
    border: '1px solid #06C167',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  userList: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    border: '1px solid #e2e8f0'
  },
  userCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    marginBottom: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  userAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #06C167, #8b5cf6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: '600',
    fontSize: '16px'
  },
  userName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#0f172a',
    margin: '0 0 4px 0'
  },
  userRole: {
    fontSize: '12px',
    color: '#64748b',
    textTransform: 'uppercase',
    fontWeight: '600'
  },
  userStats: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center'
  },
  activityCount: {
    fontSize: '14px',
    color: '#06C167',
    fontWeight: '600'
  },
  lastActivity: {
    fontSize: '12px',
    color: '#64748b'
  },
  activityTimeline: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    border: '1px solid #e2e8f0'
  },
  timelineHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    paddingBottom: '16px',
    borderBottom: '2px solid #f1f5f9'
  },
  timelineTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#0f172a',
    margin: 0
  },
  timeline: {
    position: 'relative',
    paddingLeft: '24px'
  },
  timelineItem: {
    position: 'relative',
    paddingBottom: '24px',
    borderLeft: '2px solid #e2e8f0'
  },
  timelineItemLast: {
    borderLeft: '2px solid transparent'
  },
  timelineDot: {
    position: 'absolute',
    left: '-6px',
    top: '8px',
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    border: '2px solid white',
    boxShadow: '0 0 0 2px #06C167'
  },
  activityCard: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '16px',
    marginLeft: '16px',
    transition: 'all 0.2s ease'
  },
  activityHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '8px'
  },
  activityTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1a1d29',
    margin: 0
  },
  activityTime: {
    fontSize: '12px',
    color: '#6b7280',
    fontWeight: '500'
  },
  activityDetails: {
    fontSize: '13px',
    color: '#374151',
    lineHeight: '1.5'
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#6b7280'
  },
  loadingState: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#6b7280'
  },
  badge: {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase'
  },
  successBadge: {
    background: '#dcfce7',
    color: '#166534'
  },
  errorBadge: {
    background: '#fef2f2',
    color: '#dc2626'
  },
  chartContainer: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    border: '1px solid #e2e8f0'
  }
};

// Get activity type icon
const getActivityIcon = (activityType) => {
  const icons = {
    kullanici_giris: '🔐',
    kullanici_cikis: '🚪',
    siparis_olusturuldu: '✨',
    siparis_guncellendi: '✏️',
    siparis_silindi: '🗑️',
    musteri_olusturuldu: '👤',
    musteri_guncellendi: '👤',
    urun_olusturuldu: '📦',
    fatura_olusturuldu: '📄',
    sayfa_ziyaret: '🌐',
    arama_yapildi: '🔍',
    disa_aktar: '📊',
    hata_olustu: '⚠️'
  };
  return icons[activityType] || '📝';
};

function UserActivityDashboard() {
  const { userData } = useAuth();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activities, setActivities] = useState([]);
  const [userStats, setUserStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    activityType: '',
    limit: 100
  });

  // Fetch all users
  useEffect(() => {
    if (!userData?.companyId) return;

    const fetchUsers = async () => {
      try {
        // Get all users from activities to find active users
        const activitiesRef = collection(db, 'companies', userData.companyId, 'globalActivities');
        const recentActivitiesQuery = query(
          activitiesRef,
          orderBy('timestamp', 'desc'),
          limit(1000)
        );
        
        const snapshot = await getDocs(recentActivitiesQuery);
        const userMap = new Map();
        
        snapshot.docs.forEach(doc => {
          const activity = doc.data();
          if (activity.userId && !userMap.has(activity.userId)) {
            userMap.set(activity.userId, {
              userId: activity.userId,
              userEmail: activity.userEmail,
              userName: activity.userName,
              userRole: activity.userRole,
              lastActivity: activity.timestamp,
              activityCount: 1
            });
          } else if (activity.userId) {
            const user = userMap.get(activity.userId);
            user.activityCount++;
            if (activity.timestamp > user.lastActivity) {
              user.lastActivity = activity.timestamp;
            }
          }
        });
        
        setUsers(Array.from(userMap.values()));
        setLoading(false);
      } catch (error) {
        console.error('Error fetching users:', error);
        setLoading(false);
      }
    };

    fetchUsers();
  }, [userData]);

  // Fetch selected user's activities
  useEffect(() => {
    if (!userData?.companyId || !selectedUser) return;

    const activitiesRef = collection(db, 'companies', userData.companyId, 'userActivities', selectedUser.userId, 'activities');
    let q = query(activitiesRef, orderBy('timestamp', 'desc'), limit(filters.limit));

    // Apply filters
    if (filters.activityType) {
      q = query(activitiesRef, 
        where('activityType', '==', filters.activityType),
        orderBy('timestamp', 'desc'), 
        limit(filters.limit)
      );
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const activityList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Apply date filters if specified
      let filteredActivities = activityList;
      if (filters.dateFrom || filters.dateTo) {
        filteredActivities = activityList.filter(activity => {
          const activityDate = activity.timestamp?.toDate?.() || new Date(activity.formattedDate);
          const fromDate = filters.dateFrom ? new Date(filters.dateFrom) : null;
          const toDate = filters.dateTo ? new Date(filters.dateTo) : null;
          
          if (fromDate && activityDate < fromDate) return false;
          if (toDate && activityDate > toDate) return false;
          return true;
        });
      }
      
      setActivities(filteredActivities);
      
      // Calculate user statistics
      const stats = calculateUserStats(filteredActivities);
      setUserStats(stats);
    });

    return () => unsubscribe();
  }, [userData, selectedUser, filters]);

  const calculateUserStats = (activities) => {
    const stats = {
      totalActivities: activities.length,
      todayActivities: 0,
      thisWeekActivities: 0,
      thisMonthActivities: 0,
      activityTypes: {},
      hoursActive: {},
      successRate: 0,
      averageSessionTime: 0
    };

    const today = new Date();
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    
    let successCount = 0;
    
    activities.forEach(activity => {
      const activityDate = activity.timestamp?.toDate?.() || new Date(activity.formattedDate);
      
      // Time-based counts
      if (activityDate.toDateString() === today.toDateString()) {
        stats.todayActivities++;
      }
      if (activityDate >= weekAgo) {
        stats.thisWeekActivities++;
      }
      if (activityDate >= monthAgo) {
        stats.thisMonthActivities++;
      }
      
      // Activity types
      stats.activityTypes[activity.activityType] = (stats.activityTypes[activity.activityType] || 0) + 1;
      
      // Hours active
      const hour = activityDate.getHours();
      stats.hoursActive[hour] = (stats.hoursActive[hour] || 0) + 1;
      
      // Success rate
      if (activity.details?.success !== false) {
        successCount++;
      }
    });
    
    stats.successRate = activities.length > 0 ? (successCount / activities.length * 100).toFixed(1) : 0;
    
    return stats;
  };

  const handleExportUserReport = () => {
    if (!selectedUser || activities.length === 0) return;

    const exportData = activities.map(activity => ({
      'Tarih': activity.formattedDate || activity.timestamp?.toDate?.()?.toLocaleString('tr-TR') || 'Bilinmiyor',
      'İşlem': GLOBAL_ACTIVITY_MESSAGES[activity.activityType] || activity.activityMessage || 'Bilinmeyen',
      'Modül': activity.entityType || 'Sistem',
      'Hedef': activity.entityName || '-',
      'Durum': activity.details?.success !== false ? 'Başarılı' : 'Hatalı',
      'IP Adresi': activity.ipAddress || 'Bilinmiyor',
      'Cihaz': activity.deviceInfo?.platform || 'Bilinmiyor',
      'Sayfa': activity.details?.url || '-',
      'Detaylar': JSON.stringify(activity.details || {})
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Kullanıcı Aktiviteleri');
    
    const fileName = `${selectedUser.userName}_aktivite_raporu_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '-')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const formatActivityDetails = (activity) => {
    const details = activity.details || {};
    
    return (
      <div style={styles.activityDetails}>
        <div style={{ marginBottom: '8px' }}>
          <strong>Modül:</strong> {activity.entityType || 'Sistem'}
          {activity.entityName && (
            <span style={{ marginLeft: '8px' }}>
              <strong>Hedef:</strong> {activity.entityName}
            </span>
          )}
        </div>
        
        {details.url && (
          <div style={{ marginBottom: '4px' }}>
            <strong>Sayfa:</strong> {details.url.split('/').pop() || 'Ana Sayfa'}
          </div>
        )}
        
        {activity.ipAddress && (
          <div style={{ marginBottom: '4px' }}>
            <strong>IP:</strong> {activity.ipAddress}
          </div>
        )}
        
        {details.duration && (
          <div style={{ marginBottom: '4px' }}>
            <strong>Süre:</strong> {details.duration}ms
          </div>
        )}
        
        <div style={{ marginTop: '8px' }}>
          <span style={{
            ...styles.badge,
            ...(details.success !== false ? styles.successBadge : styles.errorBadge)
          }}>
            {details.success !== false ? 'Başarılı' : 'Hatalı'}
          </span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingState}>
          <div>Kullanıcı aktiviteleri yükleniyor...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Kullanıcı Aktivite Dashboard</h1>
        <p style={styles.subtitle}>
          Tüm kullanıcıların detaylı aktivite raporları ve istatistikleri
        </p>
      </div>

      {!selectedUser ? (
        <>
          {/* Global Statistics */}
          <div style={styles.statsGrid}>
            <div style={styles.statCard}>
              <div style={styles.statNumber}>{users.length}</div>
              <div style={styles.statLabel}>Toplam Aktif Kullanıcı</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statNumber}>
                {users.reduce((sum, user) => sum + user.activityCount, 0)}
              </div>
              <div style={styles.statLabel}>Toplam Aktivite</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statNumber}>
                {users.filter(user => {
                  const lastActivity = user.lastActivity?.toDate?.() || new Date();
                  const today = new Date();
                  return lastActivity.toDateString() === today.toDateString();
                }).length}
              </div>
              <div style={styles.statLabel}>Bugün Aktif</div>
            </div>
          </div>

          {/* User List */}
          <div style={styles.userList}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#0f172a' }}>
              Kullanıcı Listesi
            </h3>
            {users.map(user => (
              <div
                key={user.userId}
                style={styles.userCard}
                onClick={() => setSelectedUser(user)}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              >
                <div style={styles.userInfo}>
                  <div style={styles.userAvatar}>
                    {user.userName?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div>
                    <div style={styles.userName}>{user.userName || user.userEmail}</div>
                    <div style={styles.userRole}>{user.userRole || 'kullanıcı'}</div>
                  </div>
                </div>
                <div style={styles.userStats}>
                  <div style={styles.activityCount}>
                    {user.activityCount} aktivite
                  </div>
                  <div style={styles.lastActivity}>
                    Son: {user.lastActivity?.toDate?.()?.toLocaleDateString('tr-TR') || 'Bilinmiyor'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          {/* Selected User Header */}
          <div style={styles.header}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={styles.title}>
                  {selectedUser.userName || selectedUser.userEmail} - Detaylı Rapor
                </h2>
                <p style={styles.subtitle}>
                  Kullanıcı Rolü: {selectedUser.userRole || 'kullanıcı'}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button 
                  style={styles.exportButton}
                  onClick={handleExportUserReport}
                >
                  📊 Excel'e Aktar
                </button>
                <button 
                  style={styles.button}
                  onClick={() => setSelectedUser(null)}
                >
                  ← Kullanıcı Listesi
                </button>
              </div>
            </div>
          </div>

          {/* User Statistics */}
          <div style={styles.statsGrid}>
            <div style={styles.statCard}>
              <div style={styles.statNumber}>{userStats.totalActivities || 0}</div>
              <div style={styles.statLabel}>Toplam Aktivite</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statNumber}>{userStats.todayActivities || 0}</div>
              <div style={styles.statLabel}>Bugün</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statNumber}>{userStats.thisWeekActivities || 0}</div>
              <div style={styles.statLabel}>Bu Hafta</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statNumber}>{userStats.thisMonthActivities || 0}</div>
              <div style={styles.statLabel}>Bu Ay</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statNumber}>{userStats.successRate || 0}%</div>
              <div style={styles.statLabel}>Başarı Oranı</div>
            </div>
          </div>

          {/* Filters */}
          <div style={styles.filterSection}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#0f172a' }}>
              Filtreler
            </h3>
            <div style={styles.filterGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Başlangıç Tarihi</label>
                <input
                  type="date"
                  style={styles.input}
                  value={filters.dateFrom}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Bitiş Tarihi</label>
                <input
                  type="date"
                  style={styles.input}
                  value={filters.dateTo}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Aktivite Türü</label>
                <select
                  style={styles.select}
                  value={filters.activityType}
                  onChange={(e) => setFilters(prev => ({ ...prev, activityType: e.target.value }))}
                >
                  <option value="">Tümü</option>
                  {Object.entries(GLOBAL_ACTIVITY_MESSAGES).map(([key, value]) => (
                    <option key={key} value={key}>{value}</option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Kayıt Sayısı</label>
                <select
                  style={styles.select}
                  value={filters.limit}
                  onChange={(e) => setFilters(prev => ({ ...prev, limit: parseInt(e.target.value) }))}
                >
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={500}>500</option>
                  <option value={1000}>1000</option>
                </select>
              </div>
            </div>
          </div>

          {/* Activity Timeline */}
          <div style={styles.activityTimeline}>
            <div style={styles.timelineHeader}>
              <h3 style={styles.timelineTitle}>Aktivite Zaman Çizelgesi</h3>
              <span style={{ fontSize: '14px', color: '#6b7280' }}>
                {activities.length} aktivite gösteriliyor
              </span>
            </div>

            {activities.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📝</div>
                <p>Seçili filtrelere uygun aktivite bulunamadı.</p>
              </div>
            ) : (
              <div style={styles.timeline}>
                {activities.map((activity, index) => (
                  <div
                    key={activity.id}
                    style={{
                      ...styles.timelineItem,
                      ...(index === activities.length - 1 ? styles.timelineItemLast : {})
                    }}
                  >
                    <div style={styles.timelineDot} />
                    <div style={styles.activityCard}>
                      <div style={styles.activityHeader}>
                        <div style={styles.activityTitle}>
                          <span style={{ marginRight: '8px' }}>
                            {getActivityIcon(activity.activityType)}
                          </span>
                          {GLOBAL_ACTIVITY_MESSAGES[activity.activityType] || activity.activityMessage || 'Bilinmeyen İşlem'}
                        </div>
                        <div style={styles.activityTime}>
                          {activity.formattedDate || 
                           (activity.timestamp?.toDate?.()?.toLocaleString('tr-TR') || 'Tarih bilinmiyor')}
                        </div>
                      </div>
                      {formatActivityDetails(activity)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default UserActivityDashboard;